import { Badge, Card, CardContent, Input, Select, Spinner } from '@crm/ui';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, FilterBar, PageHeader, Pagination } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { formatDateTime } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { type AuditEntry, type AuditQuery, useAudit, useSubscribers } from '../api';

const METHOD_TONES: Record<string, string> = {
  POST: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  PATCH: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  PUT: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  DELETE: 'bg-destructive/15 text-destructive',
};

const VAZIO: AuditQuery = {
  search: '',
  companyId: '',
  entity: '',
  from: '',
  to: '',
  page: 1,
  perPage: 20,
};

function Onde({ entry }: { entry: AuditEntry }) {
  return (
    <span className="flex flex-col">
      <span>{entry.companyName ?? 'Administracao do SaaS'}</span>
      <span className="text-xs text-muted-foreground">{entry.entity}</span>
    </span>
  );
}

function Acao({ entry }: { entry: AuditEntry }) {
  return (
    <span className="flex items-center gap-2">
      <Badge variant="secondary" className={METHOD_TONES[entry.method] ?? undefined}>
        {entry.method}
      </Badge>
      <span className="break-all text-muted-foreground">{entry.route}</span>
    </span>
  );
}

export function SaasAuditPage() {
  useDocumentTitle('Auditoria');

  const [filtros, setFiltros] = useState<AuditQuery>(VAZIO);
  const audit = useAudit(filtros);
  const assinantes = useSubscribers({ search: '', status: 'all', page: 1, perPage: 100 });

  const trocar = (mudanca: Partial<AuditQuery>) =>
    setFiltros((atual) => ({ ...atual, ...mudanca, page: mudanca.page ?? 1 }));

  const usados = [filtros.companyId, filtros.entity, filtros.from, filtros.to].filter(
    Boolean,
  ).length;

  const linhas = audit.data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<ScrollText />}
        title="Auditoria"
        description="O que os usuarios fizeram, em todos os assinantes."
      />

      <FilterBar
        className="sm:grid-cols-2 lg:grid-cols-5"
        activeCount={usados}
        search={
          <Input
            placeholder="Buscar por usuario..."
            value={filtros.search}
            onChange={(event) => trocar({ search: event.target.value })}
          />
        }
      >
        <Select
          aria-label="Assinante"
          value={filtros.companyId}
          onChange={(event) => trocar({ companyId: event.target.value })}
        >
          <option value="">Todos os assinantes</option>
          {(assinantes.data?.data ?? []).map((assinante) => (
            <option key={assinante.id} value={assinante.id}>
              {assinante.name}
            </option>
          ))}
        </Select>

        <Input
          placeholder="Modulo (ex.: customers)"
          value={filtros.entity}
          onChange={(event) => trocar({ entity: event.target.value })}
        />

        <Input
          type="date"
          aria-label="De"
          value={filtros.from}
          onChange={(event) => trocar({ from: event.target.value })}
        />

        <Input
          type="date"
          aria-label="Ate"
          value={filtros.to}
          onChange={(event) => trocar({ to: event.target.value })}
        />
      </FilterBar>

      {audit.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(audit.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="p-0">
          {audit.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : linhas.length === 0 ? (
            <div className="py-8">
              <EmptyState icon={<ScrollText />} title="Nenhuma atividade no periodo" />
            </div>
          ) : (
            <>
              <ul className="divide-y divide-border md:hidden">
                {linhas.map((entry) => (
                  <li key={entry.id} className="space-y-2 px-4 py-4">
                    <Acao entry={entry} />
                    <p className="text-sm font-medium">{entry.userName}</p>
                    <p className="text-sm text-muted-foreground">
                      {entry.companyName ?? 'Administracao do SaaS'} - {entry.entity}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(entry.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Quando</th>
                      <th className="px-4 py-3 font-medium">Usuario</th>
                      <th className="px-4 py-3 font-medium">Assinante</th>
                      <th className="px-4 py-3 font-medium">Acao</th>
                      <th className="px-4 py-3 font-medium">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((entry) => (
                      <tr key={entry.id} className="border-t border-border">
                        <td className="whitespace-nowrap px-4 py-3">
                          {formatDateTime(entry.createdAt)}
                        </td>
                        <td className="px-4 py-3">{entry.userName}</td>
                        <td className="px-4 py-3">
                          <Onde entry={entry} />
                        </td>
                        <td className="px-4 py-3">
                          <Acao entry={entry} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {entry.entityId ?? '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Pagination
        meta={audit.data?.meta}
        label="registros"
        onChange={(page) => setFiltros((atual) => ({ ...atual, page }))}
        perPage={filtros.perPage}
        onPerPageChange={(perPage) => trocar({ perPage })}
      />
    </div>
  );
}
